import { InfiniteData, useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

import { DanjiFavoriteParamsType, DanjiInfoType } from '@/app/(memul)/(list)/danji/[danjiCd]/type';
import { APT_TYPE, OFFICETEL_TYPE } from '@/const/buildingType';
import { customFetch } from '@/service/commonFetch';
import { MemulListType, SameMemulListType } from '@/service/commonType';
import { DanjiListType } from '@/service/danji/types';
import { MemulFavoriteParamsType } from '@/service/memul/list/types';
import { MemulDetailType } from '@/service/memul/memulDetailType';
import { queryKeys as memulQueryKeys } from '@/service/memul/memulQueries';
import queryKeyFactory from '@/service/queryKeyFactory';
import { useTokenStore } from '@/store/authToken';
import { useRegionStore } from '@/store/region';
import { appToFavorite, appToNeedLogin } from '@/utils/js/webView';
import { getQueryClient } from '@/utils/provider/reactQuery/getQueryClient';

const toggleYN = (value: 'Y' | 'N'): 'Y' | 'N' => (value === 'Y' ? 'N' : 'Y');

export const useFavoriteMemul = () => {
  const { mutate } = usePostFavoriteMemul();
  const { isLogin } = useTokenStore();

  const handleFavoriteClick = useCallback(
    (params: MemulFavoriteParamsType) => {
      if (isLogin) {
        mutate(params);
      } else {
        appToNeedLogin();
      }
    },
    [isLogin, mutate],
  );

  return { handleFavoriteClick };
};

/**`
 *
 * @description 매물리스트 관심 등록/삭제 - 매물리스트(/memul-list/), 매물상세(/memul/T11792585), 단지상세매물리스트(/danji/14126), 중개사매물리스트(/realtor/1342)
 */
export const usePostFavoriteMemul = (mutateCb?: { (): void }) => {
  return useMutation({
    mutationKey: ['memulFavorite'],
    mutationFn: (params: MemulFavoriteParamsType) =>
      customFetch<any>(`/member/favorites/offering/register`, {
        method: 'post',
        body: JSON.stringify(params),
      }),

    onSuccess: (data, { sOfferingSN, buildingTypeCd }) => {
      const queryClient = getQueryClient();
      //매물상세 관심업데이트
      queryClient.setQueryData(
        memulQueryKeys.memulDetail(sOfferingSN),
        (prevData: MemulDetailType) => {
          if (!prevData) return undefined;
          const currentUseYn = prevData.data.memberFavoriteInfo[0]?.useYn ?? 'N';
          return {
            ...prevData,
            data: {
              ...prevData.data,
              memberFavoriteInfo: [{ useYn: toggleYN(currentUseYn) }],
            },
          };
        },
      );
      // queryClient.invalidateQueries({ queryKey: memulQueryKeys.memulDetail(sOfferingSN) });
      //매물리스트,단지상세매물리스트,중개사매물리스트 관심업데이트
      queryClient.setQueriesData(
        { queryKey: ['memulList'] },
        (
          prevData: InfiniteData<MemulListType, number> | undefined,
        ): InfiniteData<MemulListType, number> | undefined => {
          if (!prevData) return undefined;

          return {
            ...prevData,
            pages: prevData.pages.map((page) => ({
              ...page,
              data: {
                ...page.data,
                items: page.data.items.map((item) => {
                  if (String(item.articleCd) === sOfferingSN) {
                    const currentUseYn = item.memberFavoriteInfo[0]?.useYn ?? 'N';
                    return {
                      ...item,
                      memberFavoriteInfo: [
                        {
                          ...item.memberFavoriteInfo[0],
                          useYn: toggleYN(currentUseYn),
                        },
                      ],
                    };
                  }
                  return item;
                }),
              },
            })),
          };
        },
      );

      //매물리스트 sameList, 유사매물리스트 관심업데이트
      queryClient.setQueriesData(
        { queryKey: ['memulFavorite'] },
        (prevData: SameMemulListType | undefined) => {
          if (!prevData) return undefined;
          return {
            ...prevData,
            data: {
              ...prevData.data,
              items: prevData.data.items.map((item) => {
                if (String(item.articleCd) === sOfferingSN) {
                  const currentUseYn = item.memberFavoriteInfo[0]?.useYn ?? 'N';
                  return {
                    ...item,
                    memberFavoriteInfo: [
                      {
                        ...item.memberFavoriteInfo[0],
                        useYn: toggleYN(currentUseYn),
                      },
                    ],
                  };
                }
                return item;
              }),
            },
          };
        },
      );

      // queryClient.invalidateQueries({
      //   queryKey: ['memulList', { bjdCd: areaInfo.bjdCd }],
      //   exact: false, // 부분 매칭을 허용
      // });
      if (data?.data?.message === '관심 매물 등록 되었습니다.') {
        appToFavorite({
          key: 'KEY_FAVORITE_OFFERING',
          value: Object.keys({ ...APT_TYPE, ...OFFICETEL_TYPE }).includes(buildingTypeCd)
            ? 'communal'
            : 'non_commual',
          id: sOfferingSN,
        });
      }

      if (data?.data?.message === '관심 매물 해제 되었습니다.') {
        appToFavorite({
          key: 'KEY_UNFAVORITE',
          value: 'offering',
          id: sOfferingSN,
        });
      }
      mutateCb && mutateCb();
    },
    onError(error: any) {
      if (error?.data.desc === '관심매물 최대 100개까지만 등록 가능합니다.') {
        appToFavorite({
          key: 'KEY_FAVORITE_LIMIT',
          value: 'offering',
        });
      }
    },
  });
};

export const useFavoriteDanji = () => {
  const { mutate } = usePostFavoriteDanji();
  const { isLogin } = useTokenStore();

  const handleFavoriteClick = useCallback(
    (params: DanjiFavoriteParamsType) => {
      if (isLogin) {
        mutate(params);
      } else {
        appToNeedLogin();
      }
    },
    [isLogin, mutate],
  );

  return { handleFavoriteClick };
};

/**
 *
 * @description 단지리스트 관심 등록/삭제 - 단지리스트(/memul-list/danji), 단지상세(/danji/14126)
 */
export const usePostFavoriteDanji = (mutateCb?: { (): void }) => {
  const { areaInfo } = useRegionStore();
  return useMutation({
    mutationKey: ['danjiFavorite'],
    mutationFn: (params: DanjiFavoriteParamsType) =>
      customFetch<any>(`/member/favorites/complex/register`, {
        method: 'post',
        body: JSON.stringify(params),
      }),
    onSuccess: (data, { nComplexCode, danjiNm }) => {
      const queryClient = getQueryClient();
      // 단지상세 관심업데이트
      queryClient.setQueryData(
        queryKeyFactory.danji.detail(nComplexCode),
        (prevData: DanjiInfoType) => {
          if (!prevData) return undefined;
          const currentUseYn = prevData.data.memberFavoriteInfo[0]?.useYn ?? 'N';

          return {
            ...prevData,
            data: {
              ...prevData.data,
              memberFavoriteInfo: [{ useYn: toggleYN(currentUseYn) }],
            },
          };
        },
      );
      // 단지리스트 관심 업데이트
      queryClient.setQueriesData(
        { queryKey: ['danjiList', { bjdCd: areaInfo.bjdCd }] },
        (
          prevData: InfiniteData<DanjiListType, number> | undefined,
        ): InfiniteData<DanjiListType, number> | undefined => {
          if (!prevData) return undefined;

          return {
            ...prevData,
            pages: prevData.pages.map((page) => ({
              ...page,
              data: {
                ...page.data,
                items: page.data.items.map((item) => {
                  if (String(item.danjiCd) === nComplexCode) {
                    const currentUseYn = item.memberFavoriteInfo[0]?.useYn ?? 'N';
                    return {
                      ...item,
                      memberFavoriteInfo: [
                        {
                          ...item.memberFavoriteInfo[0],
                          useYn: toggleYN(currentUseYn),
                        },
                      ],
                    };
                  }
                  return item;
                }),
              },
            })),
          };
        },
      );
      // queryClient.invalidateQueries({ queryKey: queryKeys.danjiDetail(nComplexCode) });
      // queryClient.invalidateQueries({
      //   queryKey: ['danjiList', { bjdCd: areaInfo.bjdCd }],
      //   exact: false, // 부분 매칭을 허용
      // });
      if (data?.data?.message === '관심 단지 등록 되었습니다.') {
        appToFavorite({
          key: 'KEY_FAVORITE_COMPLEX',
          value: danjiNm,
          id: nComplexCode,
        });
      }

      if (data?.data?.message === '관심 단지 해제 되었습니다.') {
        appToFavorite({
          key: 'KEY_UNFAVORITE',
          value: 'complex',
          id: nComplexCode,
        });
      }
      mutateCb && mutateCb();
    },
    onError(error: any) {
      if (error?.data.desc === '관심매물 최대 30개까지만 등록 가능합니다.') {
        appToFavorite({
          key: 'KEY_FAVORITE_LIMIT',
          value: 'complex',
        });
      }
    },
  });
};

interface FavoriteCountParamsType {
  favorites: 'offering' | 'complex';
  favorite: string;
}
export const favoriteQueryKeys = {
  count: (params: FavoriteCountParamsType) => ['favoriteCount', params],
};

const favoriteQueries = {
  // *** 매물리스트 ***
  /** @description 매물리스트 */
  count: (params: FavoriteCountParamsType) => ({
    queryKey: favoriteQueryKeys.count(params),
    queryFn: () =>
      customFetch<any>(`/member/favorites/count`, {
        params,
      }),
  }),
};

export default favoriteQueries;
